import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, Mail, ExternalLink } from "lucide-react";
import { Head as Helmet } from "vite-react-ssg";
import type { AgentProfile } from "@/hooks/useAgentBySlug";

interface AgentBrandingCardProps {
  agent: AgentProfile;
}

const AgentBrandingCard = ({ agent }: AgentBrandingCardProps) => {
  const displayName = agent.display_name || "Agent";
  const firstName = displayName.split(" ")[0];

  return (
    <>
      {/* Prevent branded pages from being indexed */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-6">
            {agent.photo_url && (
              <img
                src={agent.photo_url}
                alt={displayName}
                className="h-24 w-24 rounded-full object-cover shrink-0"
              />
            )}
            <div className="flex-1">
              <h3 className="font-heading text-xl font-bold">{displayName}</h3>
              {agent.headline && (
                <p className="text-sm text-muted-foreground mb-2">{agent.headline}</p>
              )}
              {agent.bio && (
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{agent.bio}</p>
              )}
              <div className="flex flex-wrap gap-3">
                {agent.lead_capture_url ? (
                  <Button size="sm" asChild>
                    <a
                      href={agent.lead_capture_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gap-1.5"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Get in Touch
                    </a>
                  </Button>
                ) : (
                  <>
                    {agent.phone && (
                      <Button size="sm" variant="outline" asChild>
                        <a href={`tel:${agent.phone}`} className="gap-1.5">
                          <Phone className="h-3.5 w-3.5" />
                          Contact {firstName}
                        </a>
                      </Button>
                    )}
                    {agent.email && (
                      <Button size="sm" variant="outline" asChild>
                        <a href={`mailto:${agent.email}`} className="gap-1.5">
                          <Mail className="h-3.5 w-3.5" />
                          Email
                        </a>
                      </Button>
                    )}
                  </>
                )}
                {agent.slug && (
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/agent/${agent.slug}`}>View Profile</Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default AgentBrandingCard;
